/** RFC 0263 — the document catalog + content write model. */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import {
  createProjectComments,
  createProjectOwnedDocument,
} from '../project/creation'
import { changeProject } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import { imm } from '../stored-string'
import { projectFolders, readDocumentBytes } from './reads'
import type { DocRef, DocumentDoc } from './types'
import { writeChanged } from './write-changed'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/**
 * Create a document (RFC 0263): a new Automerge doc for the content, plus a
 * catalog entry in the project. Mirrors `createSession` (content doc + registry
 * entry). Returns the document handle so the caller has its id. The registry write
 * is in-place, so concurrent creations merge losslessly.
 */
export async function createDocument(
  repo: Repo,
  projectId: string,
  opts: {
    path: string
    mime: string
    title?: string
    content?: string
    /** RFC 0267: tag this document as an instance of an app data type. */
    dataType?: string
    /** RFC 0267: an app instance's structured state (used instead of/alongside
     *  content). */
    data?: Record<string, unknown>
    /** A file's bytes (a PDF, an image), stored instead of text content. */
    bytes?: Uint8Array
    /** RFC 0313 3a: who made it, so the first row of history has a name. */
    changeMessage?: string
    /** RFC 0313 3j: false for a document made on a branch, which main's
     *  catalog learns about only when the branch merges. */
    catalog?: boolean
  },
): Promise<DocHandle<DocumentDoc>> {
  if (opts.catalog !== false) {
    const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
    await (project as { whenReady?: () => Promise<void> }).whenReady?.()
    assertDocumentOutsideSources(project.doc(), opts.path)
  }
  const now = Date.now()
  const title = opts.title ?? opts.path
  const doc = await createProjectOwnedDocument<DocumentDoc>(repo, projectId)
  const documentId = doc.documentId
  const comments = await createProjectComments(repo, projectId, documentId)
  doc.change(
    (d) => {
      d.id = documentId
      d.workspaceId = projectId
      if (comments) d.commentsId = comments.documentId
      d.path = opts.path
      d.mime = opts.mime
      d.content = opts.content ?? ''
      d.createdAt = now
      d.updatedAt = now
      if (opts.dataType) d.dataType = opts.dataType
      if (opts.data) d.data = opts.data
      if (opts.bytes) d.bytes = opts.bytes
    },
    opts.changeMessage ? { message: opts.changeMessage } : {},
  )
  if (opts.catalog === false) return doc
  await catalogDocument(repo, projectId, documentId, {
    title,
    path: opts.path,
    mime: opts.mime,
    updatedAt: now,
    ...(opts.dataType ? { dataType: opts.dataType } : {}),
  })
  return doc
}

/** Put a document in a project's catalog, or replace its entry. */
export async function catalogDocument(
  repo: Repo,
  projectId: string,
  documentId: string,
  entry: {
    title: string
    path: string
    mime: string
    updatedAt: number
    dataType?: string
  },
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  assertDocumentOutsideSources(project.doc(), entry.path)
  changeProject(project, (d) => {
    if (!d.documents) d.documents = {}
    // ASSIGNED WHOLE, never spliced, so none of these needs a text object: a
    // title is replaced rather than co-edited, and a path and a mime type are
    // machine values. One op each instead of one per character — on the
    // project document measured here, titles and paths were among the largest
    // sources of per-character writes. See `stored-string`.
    //
    // `dataType` is DELIBERATELY LEFT ALONE. It is an app's short name, a
    // handful of characters, and seven places compare it with `===` or test it
    // with `typeof`. Converting it would save about six ops per document and
    // break every one of those, which is the wrong side of the trade.
    d.documents[documentId] = {
      ...entry,
      title: imm(entry.title),
      path: imm(entry.path),
      mime: imm(entry.mime),
    }
  })
}

/**
 * Bump a document's `updatedAt` in the project CATALOG (RFC 0263).
 *
 * Every write path needs this — the sidebar sorts on it — and every write path
 * had its own copy: four of them by the time build artifacts arrived
 * (`writeDocumentContent`, `writeDocumentData`, `patchDocumentData`, `publishArtifact`).
 * A rule copied per call site is a rule with a missing copy, which is the same
 * argument the build-output guard in `writeDocumentContent` makes about itself.
 *
 * Silently does nothing when the document is not catalogued: content lives in
 * its own doc, and a write to an uncatalogued one is a caller's problem to
 * report, not a reason to fail the write that already happened.
 */
export async function touchCatalog(
  repo: Repo,
  projectId: string,
  documentId: string,
  at: number,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  changeProject(project, (d: Draft) => {
    const ref = d.documents?.[documentId]
    if (ref) ref.updatedAt = at
  })
}

/**
 * Replace an app instance's structured state (RFC 0267): `data` becomes the map
 * passed in, and `updatedAt` is bumped in the doc and the catalog.
 *
 * LAST-WRITER-WINS ACROSS PEERS, and deliberately so — but know what that costs.
 * This is the "here is the new whole state" verb, so two peers acting on one
 * instance at the same time will lose one of the two writes, silently. Measured
 * on two concurrent votes over `{alice, bob}`:
 *
 *   d.data = next            -> {alice:'', bob:'pizza'}      one vote lost
 *   per-key, whole maps      -> {alice:'thai', bob:''}       one vote lost
 *   per-key, only what moved -> {alice:'thai', bob:'pizza'}  both kept
 *
 * Writing key by key does NOT rescue this: when both callers send the full map,
 * every key is written by both and every key conflicts, so one peer simply wins
 * them all. The granularity that matters is the CALLER'S, not this function's.
 *
 * So if concurrent edits matter for your instance — a poll being voted on, a
 * board being moved around — use `patchDocumentData` and send only what changed.
 * That is what `mod.type.change` does. This function is for the case where the
 * caller genuinely owns the whole object.
 */
export async function writeDocumentData(
  repo: Repo,
  projectId: string,
  documentId: string,
  data: Record<string, unknown>,
  /** RFC 0313 3a: who made it, so the change has a name in history. */
  opts: { changeMessage?: string } = {},
): Promise<void> {
  const now = Date.now()
  const doc = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  doc.change(
    (d) => {
      // Only what changed is written: assigning the whole object rewrote every
      // string in it, 30,000 ops a save for a 40 KB deck (RFC 0346).
      writeChanged(d, [], d as never, 'data', data)
      d.updatedAt = now
    },
    opts.changeMessage ? { message: opts.changeMessage } : {},
  )
  await touchCatalog(repo, projectId, documentId, now)
}

/**
 * Overwrite a document's CONTENT (RFC 0263), and bump its `updatedAt` in both the
 * doc and the project registry (so the sidebar re-sorts).
 *
 * Named for the field it writes, because the pair was previously `writeDocument`
 * and `writeDocumentData` — which read as "write a document" versus "write a
 * document's data", implying a general write and a special one. They are neither:
 * a document holds `content` (prose, markup, source — a string people and agents
 * edit) OR `data` (the structured fields of a typed instance, which an app owns).
 * Two different fields, two writes, and picking the wrong one silently writes to
 * a field nothing reads.
 *
 * MERGES rather than replaces. `d.content = content` is a whole-value
 * assignment, so two concurrent writes resolve last-writer-wins and the loser
 * vanishes with no conflict and no warning. `A.updateText` diffs the stored
 * string against the new one and applies character-level splices, so edits to
 * different regions both survive and edits to the same region interleave.
 * Proven against this exact schema: whole-value merge of two one-line edits
 * keeps one, updateText keeps both.
 *
 * This needs no model change. `content` stays a plain string; the difference is
 * entirely in HOW it is written, which is why it applies to every document
 * write rather than to a collaborative-editor slice. Agents write these
 * documents too, so the losing write was as likely to be an agent's as a
 * person's.
 */
/** Content longer than this is written as a scalar, not spliced as text. */
export const SCALAR_CONTENT_CHARS = 200_000

export async function writeDocumentContent(
  repo: Repo,
  projectId: string,
  documentId: string,
  content: string,
  opts: {
    /**
     * Write over BUILD OUTPUT on purpose, converting it back to an ordinary
     * document (RFC 0274). Without it, a generated document refuses.
     */
    overwriteGenerated?: boolean
    /**
     * The Automerge change message (RFC 0301 §3e). An agent write carries its
     * session here, which is the whole record of who made the change: history
     * reads it back for the diff and for undo.
     */
    changeMessage?: string
  } = {},
): Promise<string | null> {
  const now = Date.now()
  const doc = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  await (doc as { whenReady?: () => Promise<void> }).whenReady?.()

  // The rule lives HERE, not in the callers.
  //
  // It was first written as a check in two call sites, and there are seven:
  // update_document, patch_document, docs.write, docs.patch, type.writeBody,
  // the cli's docs update, and the read-modify-write inside patch. Three had
  // no guard, including `patch_document`, which is simply this function with a
  // string search in front of it and is the tool most likely to be pointed at
  // a published view. That is the same mistake as the duplicated browsable
  // predicate: a rule copied per call site is a rule with a missing copy.
  if (!opts.overwriteGenerated && (doc.doc() as DocumentDoc)?.generated) {
    throw new Error(
      `${documentId} is build output (RFC 0274): writing here would discard the build. Edit the project's source and republish, or convert it back to an ordinary document on purpose.`,
    )
  }
  // A file is not text, so there is nothing for a text write to change.
  if (readDocumentBytes(doc.doc())) {
    throw new Error(
      `${documentId} is a file (${(doc.doc() as DocumentDoc).mime}), not text: save the new file as its own document instead.`,
    )
  }

  doc.change(
    (d) => {
      // `updateText` needs a TEXT field. Two ways it is not one:
      //
      //  - first write, where content is absent, and
      //  - a document previously published as an artifact (RFC 0274), whose
      //    content is an ImmutableString SCALAR. Splicing that throws
      //    "path did not refer to an object", which would make a published
      //    document permanently unwritable by this path, and only on the SECOND
      //    write, long after any first-run test passed.
      //
      // Both are handled by assigning a plain string first, which restores a
      // spliceable field. Converting an artifact back to prose loses no merge:
      // until this moment the document was generated output that nobody edited
      // concurrently, so there is nothing to preserve. Every write after this one
      // merges normally.
      // A BUNDLE-SIZED STRING IS NOT PROSE. Splicing one in costs an op per
      // character that the document keeps forever: the Text editor's 763 KB
      // bundle written once through this path was 793,000 ops on 2026-10-20,
      // the heaviest document in the daemon. Past the bound it is stored as
      // a scalar, which costs one op and merges last-writer-wins, the right
      // trade for something nobody edits by hand.
      if (content.length > SCALAR_CONTENT_CHARS) {
        ;(d as { content: unknown }).content = new A.ImmutableString(content)
      } else {
        if (typeof d.content !== 'string') d.content = ''
        A.updateText(d, ['content'], content)
      }
      // Prose now, whatever it was: a document written by hand is no longer
      // build output, and leaving the flag set would refuse every later edit.
      if (d.generated) delete d.generated
      d.updatedAt = now
    },
    opts.changeMessage ? { message: opts.changeMessage } : {},
  )
  await touchCatalog(repo, projectId, documentId, now)
  // The change this write made, so a caller can name it later (an undo). Only
  // when it is the one head: a concurrent change arriving in the same instant
  // would make the answer ambiguous, and a wrong hash undoes the wrong edit.
  const heads = A.getHeads(doc.doc() as A.Doc<unknown>)
  return heads.length === 1 ? (heads[0] ?? null) : null
}

/**
 * Publish a BUILD ARTIFACT into a document (RFC 0274).
 *
 * Not `writeDocumentContent`. That splices via `updateText` so two people editing a
 * plan both keep their edits, which is exactly right for prose and exactly
 * wrong here: a bundle is generated output that nobody edits, so the diff is
 * pure cost. Measured on a real 472KB bundle, eleven rebuilds: 977ms through
 * updateText against 121ms here, and a worst-case rebuild (most bytes moved)
 * did not finish updateText in ten minutes.
 *
 * Stored as an `ImmutableString`, which is an Automerge SCALAR rather than a
 * text CRDT: assignment is O(1), nothing is diffed, and nothing merges. This is
 * Patchwork's own choice for module contents (`string | Uint8Array |
 * ImmutableString`), reached there for the same reason.
 *
 * NOTE ON GROWTH: rewriting one document retains every previous value, so a
 * document rewritten eleven times reached 1.66MB where a fresh document per
 * push stayed at 0.15MB. Publishing into a fresh document is therefore the
 * caller's job, not this function's, and the caller must then repoint whatever
 * referenced the old one.
 *
 * A superseded document must NOT be deleted, tempting as it is for tidiness.
 * RFC 0274 §3 keeps a Mod module reference resolvable at PINNED heads, so
 * "the version I was built against" is a document id that must stay
 * resolvable; deleting on publish dangles every pin at the next build. It also
 * makes a mid-session reader worse rather than better: holding a superseded id
 * renders a stale build, while holding a deleted one resolves to nothing at
 * all. Superseded documents are storage, and storage is the cheaper problem.
 */
export async function publishArtifact(
  repo: Repo,
  projectId: string,
  documentId: string,
  content: string,
): Promise<void> {
  const now = Date.now()
  const doc = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  doc.change((d) => {
    ;(d as { content: unknown }).content = new A.ImmutableString(content)
    d.generated = true
    d.updatedAt = now
  })
  await touchCatalog(repo, projectId, documentId, now)
}

/**
 * Merge a PATCH into an app instance's structured state (RFC 0267 `change`):
 * apply each patch key field-by-field inside one Automerge change, so fields
 * the patch omits are preserved (unlike writeDocumentData, which replaces the
 * whole map). Applying keys individually also lets Automerge merge concurrent
 * patches to DIFFERENT top-level keys instead of clobbering the whole object.
 */
export async function patchDocumentData(
  repo: Repo,
  projectId: string,
  documentId: string,
  patch: Record<string, unknown>,
  /** RFC 0313 3a: who made it, so the change has a name in history. */
  opts: { changeMessage?: string } = {},
): Promise<void> {
  const now = Date.now()
  const doc = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  doc.change(
    (d) => {
      if (!d.data) d.data = {}
      const data = d.data as Record<string, unknown>
      for (const k of Object.keys(patch))
        writeChanged(d, ['data'], data, k, patch[k])
      d.updatedAt = now
    },
    opts.changeMessage ? { message: opts.changeMessage } : {},
  )
  await touchCatalog(repo, projectId, documentId, now)
}

/** Rename a document (its catalog title). Registry-only, no content hydration. */
export async function renameDocument(
  repo: Repo,
  projectId: string,
  documentId: string,
  title: string,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  changeProject(project, (d: Draft) => {
    const ref = d.documents?.[documentId]
    if (ref) ref.title = title
  })
}

/**
 * A display path in the one shape the catalog stores (RFC 0338): segments
 * joined by `/`, no leading or trailing slash, no empty segment. Returns null
 * for a path that names nothing or climbs (`..`, `.`), which a caller refuses.
 */
export function normalizeDocumentPath(path: string): string | null {
  const segments = String(path)
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
  if (segments.length === 0) return null
  if (segments.some((s) => s === '..' || s === '.')) return null
  return segments.join('/')
}

/** Source folders contain local files only, never synced documents. */
export function assertDocumentOutsideSources(
  project: ProjectDoc | undefined,
  path: string,
): void {
  const normalized = normalizeDocumentPath(path)
  if (!normalized) return
  for (const source of Object.values(project?.sources ?? {})) {
    const folder = source.folder && normalizeDocumentPath(source.folder)
    if (
      folder &&
      (normalized === folder ||
        normalized.startsWith(`${folder}/`) ||
        folder.startsWith(`${normalized}/`))
    )
      throw new Error(
        `${folder} belongs to source ${source.name}; documents cannot go there`,
      )
  }
}

/** Keep a folder in the tree even after its last document moves away. */
export async function createFolder(
  repo: Repo,
  projectId: string,
  path: string,
): Promise<{ id: string; path: string }> {
  const next = normalizeDocumentPath(path)
  if (!next) throw new Error(`"${path}" is not a folder path`)
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = project.doc()
  if (projectFolders(doc).some((folder) => folder.path === next))
    throw new Error(`${next} is already a folder`)
  if (
    Object.values(doc?.documents ?? {}).some(
      (ref) => storedPath(ref.path) === next,
    )
  )
    throw new Error(`${next} already contains a document`)
  for (const source of Object.values(doc?.sources ?? {})) {
    const folder = source.folder && normalizeDocumentPath(source.folder)
    if (folder && (next === folder || next.startsWith(`${folder}/`)))
      throw new Error(`${next} overlaps source ${source.name}`)
  }
  const id = crypto.randomUUID()
  changeProject(project, (draft) => {
    if (!draft.folders) draft.folders = {}
    draft.folders[id] = { path: next }
  })
  return { id, path: next }
}

/** Remove only an explicit folder with no children; never delete contents. */
export async function removeEmptyFolder(
  repo: Repo,
  projectId: string,
  path: string,
): Promise<void> {
  const target = normalizeDocumentPath(path)
  if (!target) throw new Error(`"${path}" is not a folder path`)
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = project.doc()
  const entry = projectFolders(doc).find((folder) => folder.path === target)
  if (!entry) throw new Error(`${target} is not an explicit folder`)
  const inside = (path: string) =>
    path === target || path.startsWith(`${target}/`)
  if (
    Object.values(doc?.documents ?? {}).some((ref) =>
      inside(storedPath(ref.path)),
    ) ||
    projectFolders(doc).some(
      (folder) => folder.id !== entry.id && inside(folder.path),
    ) ||
    Object.values(doc?.sources ?? {}).some(
      (source) => source.folder && inside(String(source.folder)),
    )
  )
    throw new Error(`${target} is not empty`)
  changeProject(project, (draft) => {
    if (draft.folders?.[entry.id]) delete draft.folders[entry.id]
  })
}

/**
 * Give a document a new display path (RFC 0338 3a). Same id, same title: a move
 * is not a rename, and nothing that named the document before stops naming it.
 *
 * The catalog entry is what the tree reads, so it is written first; the
 * document's own `path` is a copy, written after so the two agree. Moving onto
 * a path another document already has is refused, because two rows with one
 * name in one folder are two rows nobody can tell apart.
 */
export async function moveDocument(
  repo: Repo,
  projectId: string,
  documentId: string,
  path: string,
): Promise<{ id: string; path: string }> {
  const next = normalizeDocumentPath(path)
  if (!next) throw new Error(`"${path}" is not a document path`)
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  assertDocumentOutsideSources(project.doc(), next)
  const docs = project.doc()?.documents ?? {}
  const ref = docs[documentId]
  if (!ref) throw new Error(`${documentId} is not a document in this project`)
  if (String(ref.path) === next) {
    // Already there in the catalog. The document's own copy may still be
    // stale (a concurrent move wrote it last), so a repeat move repairs it.
    await writeOwnPath(repo, project, documentId)
    return { id: documentId, path: next }
  }
  const taken = Object.entries(docs).find(
    ([id, r]) => id !== documentId && storedPath(r.path) === next,
  )
  // Said the way every taken path is said (RFC 0357 3i), so a window can tell
  // a clash it may answer with a rename from a refusal it cannot.
  if (taken) throw new Error(`${next} is already taken (${taken[0]})`)
  changeProject(project, (d: Draft) => {
    const r = d.documents?.[documentId]
    if (r) r.path = next
  })
  await writeOwnPath(repo, project, documentId)
  return { id: documentId, path: next }
}

/**
 * Move or rename a folder (RFC 0338 3b): every document whose path starts with
 * `from/` now starts with `to/`. `funding` to `fundraise` renames;
 * `funding` to `company/funding` moves.
 *
 * All or nothing. Every new path is computed and checked before anything is
 * written, so a collision anywhere leaves the folder exactly as it was, and the
 * catalog rewrite is one change so a peer never sees it half-moved.
 */
export async function moveFolder(
  repo: Repo,
  projectId: string,
  from: string,
  to: string,
  opts?: {
    /**
     * The ids a caller checked before asking (a pairing's edit rule, say). If
     * the folder holds anything else by the time it moves, nothing moves:
     * a document filed into the folder during the check was never checked.
     */
    expectIds?: readonly string[]
    /**
     * RFC 0357 3i: land in a folder that is already there. A document whose
     * path is taken keeps both, under a free name; a folder entry joins the
     * one already at its path. Without it, any clash refuses the move.
     */
    merge?: boolean
  },
): Promise<{ moved: string[] }> {
  const src = normalizeDocumentPath(from)
  const dst = normalizeDocumentPath(to)
  if (!src) throw new Error(`"${from}" is not a folder path`)
  if (!dst) throw new Error(`"${to}" is not a folder path`)
  if (src === dst) return { moved: [] }
  if (dst.startsWith(`${src}/`)) {
    throw new Error(`cannot move ${src} into itself (${dst})`)
  }
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  assertDocumentOutsideSources(project.doc(), `${dst}/file`)
  const docs = project.doc()?.documents ?? {}
  const prefix = `${src}/`
  const moves = new Map<string, string>()
  for (const [id, r] of Object.entries(docs)) {
    const p = storedPath(r.path)
    if (p.startsWith(prefix)) moves.set(id, `${dst}/${p.slice(prefix.length)}`)
  }
  const folderMoves = new Map<string, string>()
  for (const [id, folder] of Object.entries(project.doc()?.folders ?? {})) {
    const path = storedPath(folder.path)
    if (path === src || path.startsWith(prefix))
      folderMoves.set(id, `${dst}${path.slice(src.length)}`)
  }
  if (moves.size === 0 && folderMoves.size === 0)
    throw new Error(`no documents or explicit folders under ${src}/`)
  if (opts?.expectIds) {
    const expected = new Set(opts.expectIds)
    const same =
      expected.size === moves.size &&
      [...moves.keys()].every((id) => expected.has(id))
    if (!same) {
      throw new Error(
        `${src}/ changed while the move was being checked; try again`,
      )
    }
  }
  // Two documents under the folder can land on one path: `old/a` and a legacy
  // `old//a` both become `new/a`. Refused, naming them, rather than written.
  const staying = new Set<string>()
  for (const [id, r] of Object.entries(docs)) {
    if (!moves.has(id)) staying.add(storedPath(r.path))
  }
  const stayingFolders = new Set(
    Object.entries(project.doc()?.folders ?? {})
      .filter(([id]) => !folderMoves.has(id))
      .map(([, folder]) => storedPath(folder.path)),
  )
  // A folder holds its path as surely as a document does (one owner per
  // path, 3i): explicit entries staying or landing, and every folder a
  // staying document sits in. A document may not be named onto one.
  const folderPaths = new Set([...stayingFolders, ...folderMoves.values()])
  for (const path of staying) {
    for (let i = path.indexOf('/'); i > 0; i = path.indexOf('/', i + 1))
      folderPaths.add(path.slice(0, i))
  }
  const landing = new Map<string, string>()
  for (const [id, wanted] of moves) {
    // Merging keeps both: a taken path becomes a free one beside it.
    const p = opts?.merge
      ? freeDocumentPath(
          wanted,
          new Set([...staying, ...folderPaths, ...landing.keys()]),
        )
      : wanted
    assertDocumentOutsideSources(project.doc(), p)
    const other = landing.get(p)
    if (other) {
      throw new Error(`${other} and ${id} would both move to ${p}`)
    }
    if (staying.has(p) || folderPaths.has(p))
      throw new Error(`moving ${src} to ${dst} would collide with ${p}`)
    landing.set(p, id)
    moves.set(id, p)
  }
  // A folder entry that lands where one already is joins it when merging.
  const joined = new Set<string>()
  for (const [id, path] of folderMoves) {
    if (staying.has(path) || landing.has(path))
      throw new Error(`moving ${src} to ${dst} would collide with ${path}`)
    if (stayingFolders.has(path)) {
      if (!opts?.merge)
        throw new Error(`moving ${src} to ${dst} would collide with ${path}`)
      joined.add(id)
    }
  }
  changeProject(project, (d: Draft) => {
    for (const [id, p] of moves) {
      const r = d.documents?.[id]
      if (r) r.path = p
    }
    for (const [id, path] of folderMoves) {
      if (!d.folders?.[id]) continue
      if (joined.has(id)) delete d.folders[id]
      else d.folders[id].path = path
    }
  })
  for (const id of moves.keys()) await writeOwnPath(repo, project, id)
  return { moved: [...moves.keys()] }
}

/**
 * A path nothing in `taken` has: the same folder and name, with `-2`,
 * `-3` before the extension until one is free (RFC 0357 3i, keep both).
 */
export function freeDocumentPath(
  path: string,
  taken: ReadonlySet<string>,
): string {
  if (!taken.has(path)) return path
  const slash = path.lastIndexOf('/')
  const folder = slash >= 0 ? path.slice(0, slash + 1) : ''
  const name = path.slice(slash + 1)
  const dot = name.lastIndexOf('.')
  const stem = dot > 0 ? name.slice(0, dot) : name
  const ext = dot > 0 ? name.slice(dot) : ''
  for (let n = 2; ; n++) {
    const next = `${folder}${stem}-${n}${ext}`
    if (!taken.has(next)) return next
  }
}

/** A catalog path as the tree reads it. Paths written before RFC 0338 were
 *  never normalised, and the tree tolerates `/a//b`, so a move must too or it
 *  skips a document the sidebar shows inside the folder. */
function storedPath(path: unknown): string {
  const p = String(path ?? '')
  return normalizeDocumentPath(p) ?? p
}

/**
 * The document's own copy of its path, set to what the catalog says NOW.
 *
 * Read after the document hydrates, not passed in: two moves of one document
 * can interleave across that await, and writing the path each was called with
 * would let the earlier move finish last and leave the copy disagreeing with
 * the catalog for good. The catalog is authoritative, so a document that
 * cannot be reached here is left stale rather than failing a move the tree
 * already shows.
 */
async function writeOwnPath(
  repo: Repo,
  project: DocHandle<ProjectDoc>,
  documentId: string,
): Promise<void> {
  try {
    const doc = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
    const current = project.doc()?.documents?.[documentId]?.path
    if (current === undefined) return
    const path = String(current)
    if (String(doc.doc()?.path ?? '') === path) return
    doc.change((d) => {
      d.path = path
    })
  } catch {
    // Unreachable content doc: the catalog already moved, which is what reads.
  }
}

/**
 * Put a catalog entry back, as it was before `deleteDocument` (RFC 0338).
 * The content doc was never removed, so restoring the entry restores the
 * document: same id, same title, path and comments. Refuses if the id is
 * already listed, so an undo cannot overwrite a newer entry.
 */
export async function restoreDocument(
  repo: Repo,
  projectId: string,
  documentId: string,
  ref: DocRef,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  assertDocumentOutsideSources(project.doc(), String(ref.path))
  if (project.doc()?.documents?.[documentId]) {
    throw new Error(`${documentId} is already in this project`)
  }
  const entry: Record<string, unknown> = {
    title: String(ref.title),
    path: String(ref.path),
    mime: String(ref.mime),
    updatedAt: Number(ref.updatedAt) || Date.now(),
  }
  if (ref.dataType) entry.dataType = String(ref.dataType)
  changeProject(project, (d: Draft) => {
    if (!d.documents) d.documents = {}
    d.documents[documentId] = entry
  })
}

/** Remove a document from the project catalog (RFC 0263). The content doc is
 *  left to GC by reachability; this is the registry unlink the sidebar reads. */
export async function deleteDocument(
  repo: Repo,
  projectId: string,
  documentId: string,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  changeProject(project, (d) => {
    if (d.documents) delete d.documents[documentId]
  })
}
